import { useState, type FormEvent } from 'react'
import { CURRENCY_DISPLAYS, DEFAULT_CURRENCY_DISPLAY } from '@billiard/shared'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { CURRENCY_DISPLAY_KEY } from '@/context/ClubContext'
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  ImageUpload,
  Loading,
  Modal,
  PageHead,
  SectionHead
} from '@/components/ui'
import { attachUploads, deleteImage, uploadImage } from '@/lib/uploads'
import { SubscriptionActions } from '@/components/subscription'
import { dateTime, dateOnly } from '@/lib/format'
import { Money } from '@/lib/currency'
import type { StaffRole } from '@/auth/storage'

// Раздел доступен только super_admin (роут под <RequireAuth roles={['super_admin']}>).
// Запросы идут на /v1/clubs* напрямую — JWT уже super_admin, ?clubId не нужен.

// support/manager исключены наравне с super_admin: это кросс-клубные роли Help
// (club_id = null), сотрудником конкретного клуба такой роли не бывает.
type StaffRoleClub = Exclude<StaffRole, 'super_admin' | 'support' | 'manager'>

interface Club {
  id: string
  name: string
  timezone: string | null
  /** Тестовый клуб (демо/отладка) — менять может только super_admin. */
  isTest: boolean
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  // Реквизиты из settings (обогащаются на бэкенде в listClubs).
  currencyDisplay: 'symbol' | 'code' | 'short'
  clubCurrency: string | null
  unp: string | null
  orgName: string | null
}

interface Device {
  id: string
  clubId: string
  name: string
  revoked: boolean
  lastSeenAt: number | null
  createdAt: number
  licenseExpiresAt: number | null
  /** Отпечаток ПК: одинаковый = тот же компьютер (null у активаций до обновления). */
  machineId: string | null
}

interface Staff {
  id: string
  email: string
  name: string
  role: StaffRoleClub
  clubId: string
  /** Аккаунт заблокирован: вход запрещён, история сохранена. null = активен. */
  disabledAt: number | null
  createdAt: number
}

/** Ответ провижининга терминала: токен отдаётся plaintext один раз. */
interface DeviceCreated {
  device: Device
  token: string
}

/** Доп. модуль из каталога (Финансы → Модули) — нужен для чек-листа при заведении клуба. */
interface FinanceModuleRow {
  id: string
  name: string
  monthlyPrice: number
}

const STAFF_ROLES: StaffRoleClub[] = ['owner', 'admin', 'viewer', 'accountant']
const STAFF_ROLE_LABEL: Record<StaffRoleClub, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  viewer: 'Наблюдатель',
  accountant: 'Бухгалтер'
}

// ─────────── Помощники онбординга нового клуба ───────────

/** Реквизиты компании (ключи совпадают с формой «Настройки компании» и онбордингом терминала). */
const REQUISITE_FIELDS = [
  { key: 'orgName', label: 'Юридическое название', ph: 'ООО «Данели Фуд»' },
  { key: 'clubCountry', label: 'Страна', ph: 'Беларусь' },
  { key: 'clubCity', label: 'Город', ph: 'Минск' },
  { key: 'clubAddress', label: 'Юридический адрес', ph: 'ул. Советская, 56' },
  { key: 'unp', label: 'УНП', ph: '491512215' },
  { key: 'clubCurrency', label: 'Валюта', ph: 'BYN' },
  { key: 'clubWebsite', label: 'Веб-сайт', ph: 'https://example.by' },
  { key: 'clubEmail', label: 'E-mail', ph: 'otchet@example.by' },
  { key: 'clubPhone', label: 'Телефон', ph: '+375 25 000-00-00' },
  { key: 'shiftEndTime', label: 'Время окончания смены', ph: '03:00' }
] as const

/** value → человекочитаемая подпись режима валюты (для колонки в таблице клубов). */
const CURRENCY_LABEL: Record<string, string> = Object.fromEntries(
  CURRENCY_DISPLAYS.map((d) => [d.value, d.label])
)

// Транслитерация RU→latin для подсказки публичного slug.
const TRANSLIT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya'
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .split('')
    .map((ch) => (ch in TRANSLIT ? TRANSLIT[ch] : ch))
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function Clubs(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Супер-админ"
        title="Клубы"
        lead="Управление клубами-арендаторами и их терминалами: провижининг клуба, выпуск device-токенов и backoffice-доступы сотрудников."
      />
      <ClubsManager />
    </>
  )
}

function ClubsManager(): React.JSX.Element {
  const list = useFetch<Club[]>('/v1/clubs')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Club | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Club | null>(null)
  const del = useMutate()

  const rows = list.data ?? []
  const selected = rows.find((c) => c.id === selectedId) ?? null

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/clubs/${deleting.id}`))
    if (ok) {
      if (selectedId === deleting.id) setSelectedId(null)
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} клуб(ов)</span>
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый клуб
          </button>
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Клубов ещё нет. Создайте первый." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable rtable--clickable">
            <thead>
              <tr>
                <th>Название</th>
                <th>Юр. название</th>
                <th>УНП</th>
                <th>Валюта</th>
                <th>Часовой пояс</th>
                <th>Создан</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className={c.id === selectedId ? 'is-selected' : undefined}
                  onClick={() => setSelectedId((id) => (id === c.id ? null : c.id))}
                >
                  <td>
                    {c.name}
                    {c.isTest && (
                      <span className="tag" style={{ marginLeft: 8 }}>
                        тест
                      </span>
                    )}
                  </td>
                  <td>{c.orgName || '—'}</td>
                  <td>{c.unp || '—'}</td>
                  <td>{CURRENCY_LABEL[c.currencyDisplay] ?? c.currencyDisplay}</td>
                  <td>{c.timezone || '—'}</td>
                  <td>{dateOnly(c.createdAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions">
                      <IconButton
                        title={`Изменить клуб «${c.name}»`}
                        aria-label="Изменить клуб"
                        onClick={() => setEditing(c)}
                      >
                        {Icon.edit}
                      </IconButton>
                      <IconButton
                        danger
                        title={`Удалить клуб «${c.name}»`}
                        aria-label="Удалить клуб"
                        onClick={() => setDeleting(c)}
                      >
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <ClubDetail key={selected.id} club={selected} />}

      {editing === 'new' && (
        <ClubOnboardWizard
          onClose={() => setEditing(null)}
          onCreated={(club) => {
            setEditing(null)
            list.reload()
            setSelectedId(club.id)
          }}
        />
      )}

      {editing && editing !== 'new' && (
        <ClubForm
          club={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить клуб"
          message={`Удалить клуб «${deleting.name}»? Данные и факты клуба сохранятся (мягкое удаление), но клуб скроется из списка.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function ClubForm({
  club,
  onClose,
  onSaved
}: {
  club: Club | null
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = club !== null
  const [name, setName] = useState(club?.name ?? '')
  const [timezone, setTimezone] = useState(club?.timezone ?? '')
  const [isTest, setIsTest] = useState(club?.isTest ?? false)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название клуба.')
    setValidation(null)

    const body = { name: trimmed, timezone: timezone.trim() || null, isTest }
    const ok = await save.mutate(() =>
      isEdit ? api.patch(`/v1/clubs/${club.id}`, body) : api.post('/v1/clubs', body)
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить клуб' : 'Новый клуб'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="club-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      <form id="club-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="c-name">
              Название
            </label>
            <input
              id="c-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. 8Ball Center"
              autoFocus
            />
          </div>
          <div className="field field--full">
            <label className="field__label" htmlFor="c-tz">
              Часовой пояс
            </label>
            <input
              id="c-tz"
              className="input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="напр. Europe/Minsk (необязательно)"
            />
          </div>
          <label
            className="field field--full"
            style={{ display: 'flex', gap: 10, alignItems: 'center' }}
          >
            <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
            <span className="field__label" style={{ margin: 0 }}>
              Тестовый клуб (демо/отладка)
            </span>
          </label>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ─────────────────────── Мастер создания нового клуба ───────────────────────

/**
 * Многошаговый онбординг клуба для super_admin: создаёт клуб (POST /v1/clubs),
 * затем заливает реквизиты и карточку каталога в settings (PUT /v1/settings/:key
 * с ?clubId нового клуба). Логотип — публичный URL (без загрузки файла).
 */
function ClubOnboardWizard({
  onClose,
  onCreated
}: {
  onClose: () => void
  onCreated: (club: Club) => void
}): React.JSX.Element {
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState('Europe/Minsk')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [isTest, setIsTest] = useState(false)
  const [req, setReq] = useState<Record<string, string>>({
    clubCountry: 'Беларусь',
    clubCurrency: 'BYN',
    shiftEndTime: '03:00'
  })
  const [currencyDisplay, setCurrencyDisplay] = useState<string>(DEFAULT_CURRENCY_DISPLAY)
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set())
  const modulesList = useFetch<FinanceModuleRow[]>('/v1/modules', undefined, [])
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const toggleModule = (id: string): void => {
    setSelectedModules((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const onName = (v: string): void => {
    setName(v)
    if (!slugTouched) setSlug(slugify(v))
    setValidation(null)
  }
  const onSlug = (v: string): void => {
    setSlug(slugify(v))
    setSlugTouched(true)
    setValidation(null)
  }
  const setR = (k: string, v: string): void => setReq((p) => ({ ...p, [k]: v }))

  const goNext = (): void => {
    if (step === 0) {
      if (!name.trim()) return setValidation('Укажите название клуба.')
      if (!slug.trim()) return setValidation('Укажите адрес страницы (латиницей).')
    }
    setValidation(null)
    setStep((s) => s + 1)
  }

  const onSubmit = async (): Promise<void> => {
    if (!name.trim()) {
      setStep(0)
      return setValidation('Укажите название клуба.')
    }
    if (!slug.trim()) {
      setStep(0)
      return setValidation('Укажите адрес страницы (латиницей).')
    }
    setValidation(null)

    let created: Club | null = null
    const ok = await save.mutate(async () => {
      const club = await api.post<Club>('/v1/clubs', {
        name: name.trim(),
        timezone: timezone.trim() || null,
        isTest
      })
      const opts = { query: { clubId: club.id } }
      const entries: { key: string; value: string }[] = [
        { key: 'clubName', value: name.trim() },
        { key: 'publicSlug', value: slug.trim() },
        { key: 'publicBookingEnabled', value: enabled ? '1' : '0' },
        { key: 'clubDescription', value: description.trim() },
        { key: 'clubLogoUrl', value: logoUrl.trim() },
        { key: CURRENCY_DISPLAY_KEY, value: currencyDisplay },
        ...REQUISITE_FIELDS.map((f) => ({ key: f.key, value: (req[f.key] ?? '').trim() }))
      ]
      // Ключевые поля пишем всегда; пустые необязательные — пропускаем.
      const always = new Set([
        'clubName',
        'publicSlug',
        'publicBookingEnabled',
        CURRENCY_DISPLAY_KEY
      ])
      for (const e of entries) {
        if (!e.value && !always.has(e.key)) continue
        await api.put(`/v1/settings/${encodeURIComponent(e.key)}`, { value: e.value }, opts)
      }
      for (const moduleId of selectedModules) {
        await api.put(`/v1/modules/club/${moduleId}`, { enabled: true }, opts)
      }
      // Логотип грузился до создания клуба и лежит «ничьим» — привязываем, иначе
      // он не удалится вместе с клубом. Не критично для формы: падение здесь
      // отменило бы уже созданный клуб, поэтому глушим.
      await attachUploads([logoUrl.trim()], club.id).catch(() => undefined)
      created = club
    })
    if (ok && created) onCreated(created)
  }

  return (
    <Modal
      title="Новый клуб"
      onClose={onClose}
      wide
      footer={
        <>
          {step === 0 ? (
            <button
              className="btn btn--ghost"
              onClick={onClose}
              type="button"
              disabled={save.saving}
            >
              Отмена
            </button>
          ) : (
            <button
              className="btn btn--ghost"
              onClick={() => setStep((s) => s - 1)}
              type="button"
              disabled={save.saving}
            >
              Назад
            </button>
          )}
          {step < 2 ? (
            <button className="btn btn--primary" onClick={goNext} type="button">
              Далее
            </button>
          ) : (
            <button
              className="btn btn--primary"
              onClick={() => void onSubmit()}
              type="button"
              disabled={save.saving}
            >
              {save.saving && <span className="spin" />}
              Создать клуб
            </button>
          )}
        </>
      }
    >
      <div className="wizard-steps" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {['Клуб и каталог', 'Реквизиты', 'Модули'].map((label, i) => (
          <div
            key={label}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 12,
              color: i === step ? 'var(--accent, #c9a84c)' : 'var(--text-2, #888)',
              borderTop: `2px solid ${i <= step ? 'var(--accent, #c9a84c)' : 'var(--border, #333)'}`,
              paddingTop: 8
            }}
          >
            {i + 1}. {label}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="ob-name">
              Название клуба
            </label>
            <input
              id="ob-name"
              className="input"
              value={name}
              onChange={(e) => onName(e.target.value)}
              placeholder="напр. 8Ball Center"
              autoFocus
            />
          </div>
          <div className="field field--full">
            <label className="field__label" htmlFor="ob-slug">
              Адрес страницы бронирования (slug)
            </label>
            <input
              id="ob-slug"
              className="input"
              style={{ fontFamily: 'var(--f-mono)' }}
              value={slug}
              onChange={(e) => onSlug(e.target.value)}
              placeholder="8ball-center"
            />
            <span className="field__hint">
              Латиница, цифры и дефис. Страница: <code>/{slug || 'slug'}</code>
            </span>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="ob-tz">
              Часовой пояс
            </label>
            <input
              id="ob-tz"
              className="input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="Europe/Minsk"
            />
          </div>
          <label
            className="field"
            style={{ display: 'flex', gap: 10, alignItems: 'center', alignSelf: 'flex-end' }}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="field__label" style={{ margin: 0 }}>
              Принимать онлайн-брони
            </span>
          </label>
          <label
            className="field field--full"
            style={{ display: 'flex', gap: 10, alignItems: 'center' }}
          >
            <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
            <span className="field__label" style={{ margin: 0 }}>
              Тестовый клуб (демо/отладка)
            </span>
          </label>
          <div className="field field--full">
            <label className="field__label" htmlFor="ob-desc">
              Краткое описание (для карточки)
            </label>
            <input
              id="ob-desc"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Уютный клуб в центре города"
            />
          </div>
          <div className="field field--full">
            <label className="field__label">Логотип клуба</label>
            <ImageUpload
              value={logoUrl}
              onChange={setLogoUrl}
              // Клуба ещё нет — картинка загружается «ничьей» и привязывается
              // к нему сразу после создания (attachUploads в onSubmit).
              onPick={(file) => uploadImage(file, 'club-logo')}
              onRemove={(url) => deleteImage(url)}
              hint="Показывается на сайте брони и в Telegram. JPEG, PNG, GIF или WebP, до 5 МБ."
            />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="form-grid">
          {REQUISITE_FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label className="field__label" htmlFor={`ob-${f.key}`}>
                {f.label}
              </label>
              <input
                id={`ob-${f.key}`}
                className="input"
                value={req[f.key] ?? ''}
                placeholder={f.ph}
                onChange={(e) => setR(f.key, e.target.value)}
              />
            </div>
          ))}
          <div className="field">
            <label className="field__label" htmlFor="ob-currencyDisplay">
              Отображение валюты
            </label>
            <select
              id="ob-currencyDisplay"
              className="input"
              value={currencyDisplay}
              onChange={(e) => setCurrencyDisplay(e.target.value)}
            >
              {CURRENCY_DISPLAYS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          {modulesList.loading && <Loading />}
          {modulesList.error && <ErrorState message={modulesList.error} />}
          {!modulesList.loading && (modulesList.data ?? []).length === 0 && (
            <EmptyState message="Каталог модулей пуст — добавьте модули в разделе «Финансы → Модули»." />
          )}
          {(modulesList.data ?? []).map((m) => (
            <label
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '9px 0',
                borderTop: '1px solid var(--border, #333)',
                fontSize: 13.5,
                cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={selectedModules.has(m.id)}
                onChange={() => toggleModule(m.id)}
              />
              {m.name}
              <span className="muted" style={{ marginLeft: 'auto' }}>
                <Money value={m.monthlyPrice} />
                /мес
              </span>
            </label>
          ))}
          {(modulesList.data ?? []).length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 12,
                paddingTop: 10,
                borderTop: '1px solid var(--border, #333)',
                fontSize: 13
              }}
            >
              <span className="muted">Модули включены: {selectedModules.size}</span>
              <span>
                Итого модули:{' '}
                <Money
                  value={(modulesList.data ?? [])
                    .filter((m) => selectedModules.has(m.id))
                    .reduce((sum, m) => sum + m.monthlyPrice, 0)}
                />
                /мес
              </span>
            </div>
          )}
        </div>
      )}

      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

function ClubDetail({ club }: { club: Club }): React.JSX.Element {
  // Продление подписки меняет сроки терминалов — после него перечитываем список.
  const [rev, setRev] = useState(0)
  return (
    <div className="club-detail">
      {/* Тот же компонент, что на странице «Лицензии» — чтобы управление подпиской
          не разъехалось между двумя экранами. */}
      <SubscriptionActions clubId={club.id} onChanged={() => setRev((r) => r + 1)} />
      <DevicesSection key={rev} clubId={club.id} clubName={club.name} />
      <IkassaSection clubId={club.id} />
      <StaffSection clubId={club.id} />
    </div>
  )
}

// ─────────────────────────── iKassa (АИС ПКС, облачный отчёт для сверки) ───────────────────────────
//
// ОТДЕЛЬНАЯ вещь от локальной iKassa Smart X (печать чека по LAN — настраивается
// на самом терминале клуба, этой секции не касается). Здесь — креды облачного
// API для сверки фискализации бухгалтером (см. /v1/reports/fiscal-reconciliation).
// Реального вызова к iKassa пока нет (нет технической спецификации, см.
// IkassaAdminService на бэкенде) — «Проверить подключение» честно об этом сообщает.

interface IkassaRegisterRow {
  id: string
  registerNumber: string
  label: string
  active: boolean
}

interface IkassaIntegration {
  clubId: string
  enabled: boolean
  login: string
  hasSecret: boolean
  unp: string | null
  registers: IkassaRegisterRow[]
  lastSyncAt: number | null
  lastSyncStatus: 'ok' | 'error' | null
  lastSyncError: string | null
  updatedAt: number
}

function IkassaSection({ clubId }: { clubId: string }): React.JSX.Element {
  const info = useFetch<IkassaIntegration>(`/v1/ikassa/admin`, { clubId }, [clubId])
  const save = useMutate()
  const test = useMutate()
  const regAct = useMutate()

  const [enabled, setEnabled] = useState(false)
  const [login, setLogin] = useState('')
  const [secret, setSecret] = useState('')
  const [unp, setUnp] = useState('')
  const [testMsg, setTestMsg] = useState<string | null>(null)
  const [regNumber, setRegNumber] = useState('')
  const [regLabel, setRegLabel] = useState('')
  const [addingRegister, setAddingRegister] = useState(false)

  // Подхватываем форму из ответа сервера (при первом успешном фетче) —
  // тот же приём, что в Telegram.tsx: не перетираем то, что уже печатает
  // пользователь, при повторных useFetch-догрузках.
  const [hydrated, setHydrated] = useState(false)
  if (info.data && !hydrated) {
    setEnabled(info.data.enabled)
    setLogin(info.data.login)
    setUnp(info.data.unp ?? '')
    setHydrated(true)
  }

  const onSave = async (): Promise<void> => {
    setTestMsg(null)
    const ok = await save.mutate(() =>
      api.put(
        `/v1/ikassa/admin`,
        { enabled, login: login.trim(), secret: secret.trim() || undefined, unp: unp.trim() || null },
        { query: { clubId } }
      )
    )
    if (ok) {
      setSecret('')
      info.reload()
    }
  }

  const onTest = async (): Promise<void> => {
    setTestMsg(null)
    await test.mutate(async () => {
      const res = await api.post<{ ok: boolean; message: string }>(`/v1/ikassa/admin/test`, undefined, {
        query: { clubId }
      })
      setTestMsg(res.message)
    })
  }

  const onAddRegister = async (): Promise<void> => {
    if (!regNumber.trim()) return
    const ok = await regAct.mutate(() =>
      api.post(
        `/v1/ikassa/admin/registers`,
        { registerNumber: regNumber.trim(), label: regLabel.trim() || undefined },
        { query: { clubId } }
      )
    )
    if (ok) {
      setRegNumber('')
      setRegLabel('')
      setAddingRegister(false)
      info.reload()
    }
  }

  const onRemoveRegister = async (id: string): Promise<void> => {
    const ok = await regAct.mutate(() =>
      api.del(`/v1/ikassa/admin/registers/${id}`, { query: { clubId } })
    )
    if (ok) info.reload()
  }

  return (
    <section className="detail-block">
      <SectionHead
        title="Интеграция с iKassa"
        meta="Облачный отчёт кассы (АИС ПКС) — для сверки фискализации, печати чека не касается"
      />

      {info.loading && <Loading />}
      {info.error && <ErrorState message={info.error} />}

      {info.data && (
        <>
          <div className="form-grid">
            <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span className="field__label" style={{ margin: 0 }}>
                Интеграция включена
              </span>
            </label>
            <div className="field">
              <label className="field__label" htmlFor="ik-login">
                Логин личного кабинета iKassa
              </label>
              <input
                id="ik-login"
                className="input"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="club@example.by"
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="ik-secret">
                API-ключ / пароль
              </label>
              <input
                id="ik-secret"
                className="input"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={info.data.hasSecret ? '•••• сохранён — оставьте пустым, чтобы не менять' : 'не задан'}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="ik-unp">
                УНП (если отличается от реквизитов клуба)
              </label>
              <input
                id="ik-unp"
                className="input"
                value={unp}
                onChange={(e) => setUnp(e.target.value)}
                placeholder="491512215"
              />
            </div>
          </div>

          <div className="toolbar toolbar--compact">
            <div className="toolbar__right" style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn--ghost btn--sm" disabled={test.saving} onClick={() => void onTest()}>
                Проверить подключение
              </button>
              <button className="btn btn--primary btn--sm" disabled={save.saving} onClick={() => void onSave()}>
                Сохранить
              </button>
            </div>
          </div>
          {save.error && <ErrorState message={save.error} />}
          {testMsg && <p className="muted" style={{ fontSize: 12.5 }}>{testMsg}</p>}
          {!testMsg && info.data.lastSyncStatus && (
            <p className="muted" style={{ fontSize: 12.5 }}>
              Последняя проверка: {info.data.lastSyncStatus === 'ok' ? 'успешно' : 'ошибка'}
              {info.data.lastSyncAt ? `, ${dateTime(info.data.lastSyncAt)}` : ''}
              {info.data.lastSyncError ? ` — ${info.data.lastSyncError}` : ''}
            </p>
          )}

          <div className="toolbar toolbar--compact" style={{ marginTop: 18 }}>
            <div className="toolbar__left">
              <span className="muted">Кассы клуба в АИС ПКС ({info.data.registers.length})</span>
            </div>
            <div className="toolbar__right">
              <button className="btn btn--ghost btn--sm" onClick={() => setAddingRegister((v) => !v)}>
                {Icon.plus} Добавить кассу
              </button>
            </div>
          </div>

          {addingRegister && (
            <div className="toolbar toolbar--compact">
              <input
                className="input"
                style={{ maxWidth: 200 }}
                placeholder="Номер кассы"
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value)}
              />
              <input
                className="input"
                style={{ maxWidth: 220 }}
                placeholder="Метка (Стойка ресепшена)"
                value={regLabel}
                onChange={(e) => setRegLabel(e.target.value)}
              />
              <button
                className="btn btn--primary btn--sm"
                disabled={regAct.saving || !regNumber.trim()}
                onClick={() => void onAddRegister()}
              >
                Добавить
              </button>
            </div>
          )}
          {regAct.error && <ErrorState message={regAct.error} />}

          {info.data.registers.length === 0 ? (
            <EmptyState message="Кассы не добавлены — сверка по официальному отчёту недоступна, пока список пуст." />
          ) : (
            <div className="table-wrap">
              <table className="rtable">
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th>Метка</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {info.data.registers.map((r) => (
                    <tr key={r.id}>
                      <td>{r.registerNumber}</td>
                      <td>{r.label || '—'}</td>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={regAct.saving}
                          onClick={() => void onRemoveRegister(r.id)}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}

// ─────────────────────────── Терминалы ───────────────────────────
function DevicesSection({
  clubId,
  clubName
}: {
  clubId: string
  clubName: string
}): React.JSX.Element {
  const list = useFetch<Device[]>(`/v1/clubs/${clubId}/devices`, undefined, [clubId])
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<Device | null>(null)
  const [deleting, setDeleting] = useState<Device | null>(null)
  const [issued, setIssued] = useState<DeviceCreated | null>(null)
  const revoke = useMutate()
  const act = useMutate()

  const rows = list.data ?? []

  const onRevokeConfirm = async (): Promise<void> => {
    if (!revoking) return
    const ok = await revoke.mutate(() =>
      api.post(`/v1/clubs/${clubId}/devices/${revoking.id}/revoke`)
    )
    if (ok) {
      setRevoking(null)
      list.reload()
    }
  }

  /** Снять отзыв — раньше вернуть терминал можно было только новым ключом. */
  const onRestore = async (d: Device): Promise<void> => {
    const ok = await act.mutate(() => api.post(`/v1/clubs/${clubId}/devices/${d.id}/restore`))
    if (ok) list.reload()
  }

  const onRename = async (d: Device): Promise<void> => {
    const next = window.prompt('Название терминала', d.name)
    if (next == null || !next.trim() || next.trim() === d.name) return
    const ok = await act.mutate(() =>
      api.patch(`/v1/clubs/${clubId}/devices/${d.id}`, { name: next.trim() })
    )
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await act.mutate(() => api.del(`/v1/clubs/${clubId}/devices/${deleting.id}`))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <section className="detail-block">
      <SectionHead title="Терминалы" meta={`${rows.length} шт.`} />
      <div className="toolbar toolbar--compact">
        <div className="toolbar__left">
          <span className="muted">{clubName}</span>
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
            {Icon.plus}
            Выпустить токен
          </button>
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Терминалов ещё нет. Выпустите токен для первого." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Терминал</th>
                <th>Статус</th>
                <th>Лицензия до</th>
                <th>ПК</th>
                <th>Активность</th>
                <th>Создан</th>
                <th style={{ width: 220 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} style={d.revoked ? { opacity: 0.6 } : undefined}>
                  <td>{d.name}</td>
                  <td>
                    {d.revoked ? (
                      <span className="status">
                        <span className="dot dot--off" />
                        отозван
                      </span>
                    ) : (
                      <span className="status">
                        <span className="dot dot--on" />
                        активен
                      </span>
                    )}
                  </td>
                  <td>
                    {d.licenseExpiresAt ? (
                      dateOnly(d.licenseExpiresAt)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {/* Короткий отпечаток ПК: по нему видно, что несколько строк —
                      это один и тот же компьютер (или, наоборот, разные). */}
                  <td title={d.machineId ?? 'отпечаток неизвестен (активация до обновления)'}>
                    {d.machineId ? (
                      <code style={{ fontSize: 12 }}>{d.machineId.slice(0, 8)}</code>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{dateTime(d.lastSeenAt)}</td>
                  <td>{dateOnly(d.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={act.saving}
                        onClick={() => void onRename(d)}
                        title="Переименовать терминал — как он подписан в списке устройств"
                      >
                        Переименовать
                      </button>
                      {d.revoked ? (
                        <>
                          <button
                            className="btn btn--primary btn--sm"
                            disabled={act.saving}
                            onClick={() => void onRestore(d)}
                          >
                            Восстановить
                          </button>
                          <button
                            className="btn btn--ghost btn--sm"
                            disabled={act.saving}
                            onClick={() => setDeleting(d)}
                            title="Удалить пустую строку терминала (если по нему нет данных)"
                          >
                            Удалить
                          </button>
                        </>
                      ) : (
                        <button className="btn btn--ghost btn--sm" onClick={() => setRevoking(d)}>
                          Отозвать
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <DeviceForm
          clubId={clubId}
          onClose={() => setCreating(false)}
          onIssued={(res) => {
            setCreating(false)
            setIssued(res)
            list.reload()
          }}
        />
      )}

      {issued && (
        <TokenReveal
          token={issued.token}
          deviceName={issued.device.name}
          onClose={() => setIssued(null)}
        />
      )}

      {revoking && (
        <ConfirmDialog
          title="Отозвать терминал"
          message={`Отозвать терминал «${revoking.name}»? Доступ закроется сразу, но отзыв обратим — терминал можно восстановить этой же кнопкой, новый ключ не нужен.`}
          confirmLabel="Отозвать"
          busy={revoke.saving}
          error={revoke.error}
          onConfirm={() => void onRevokeConfirm()}
          onCancel={() => setRevoking(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить терминал"
          message={`Удалить строку терминала «${deleting.name}»? Это нужно только для «призраков», оставшихся от прежних переактиваций. Если по терминалу есть данные, сервер удалить не даст.`}
          confirmLabel="Удалить"
          busy={act.saving}
          error={act.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </section>
  )
}

function DeviceForm({
  clubId,
  onClose,
  onIssued
}: {
  clubId: string
  onClose: () => void
  onIssued: (res: DeviceCreated) => void
}): React.JSX.Element {
  const [name, setName] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите имя терминала.')
    setValidation(null)

    let res: DeviceCreated | null = null
    const ok = await save.mutate(async () => {
      res = await api.post<DeviceCreated>(`/v1/clubs/${clubId}/devices`, { name: trimmed })
    })
    if (ok && res) onIssued(res)
  }

  return (
    <Modal
      title="Новый терминал"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="device-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Выпустить токен
          </button>
        </>
      }
    >
      <form id="device-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="d-name">
              Имя терминала
            </label>
            <input
              id="d-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Касса 1"
              autoFocus
            />
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

function TokenReveal({
  token,
  deviceName,
  onClose
}: {
  token: string
  deviceName: string
  onClose: () => void
}): React.JSX.Element {
  const [copied, setCopied] = useState(false)

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
    } catch {
      /* буфер обмена недоступен — токен всё равно виден для ручного копирования */
    }
  }

  return (
    <Modal
      title="Токен терминала"
      onClose={onClose}
      footer={
        <button className="btn btn--primary" onClick={onClose} type="button">
          Готово
        </button>
      }
    >
      <p className="modal__text">
        Токен для терминала «{deviceName}» выпущен. Вставьте его в настройки облачного синка на
        терминале.
      </p>
      <div className="token-box">
        <code>{token}</code>
        <button className="btn btn--ghost btn--sm" onClick={() => void onCopy()} type="button">
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
      </div>
      <div className="form-error" style={{ marginTop: 14 }}>
        ⚠ Токен показывается один раз — сохраните его сейчас. Повторно его узнать нельзя,
        потребуется выпускать новый.
      </div>
    </Modal>
  )
}

// ─────────────────────────── Сотрудники ───────────────────────────
function StaffSection({ clubId }: { clubId: string }): React.JSX.Element {
  const list = useFetch<Staff[]>(`/v1/clubs/${clubId}/staff`, undefined, [clubId])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Staff | null>(null)
  const [deleting, setDeleting] = useState<Staff | null>(null)
  const act = useMutate()

  const rows = list.data ?? []

  // Правка/блокировка/удаление идут в общий staff-API (/v1/staff/:id?clubId=…) —
  // тот же, которым владелец клуба управляет своей командой на странице «Команда».
  const onToggleDisabled = async (s: Staff): Promise<void> => {
    const ok = await act.mutate(() =>
      api.patch(`/v1/staff/${s.id}`, { disabled: !s.disabledAt }, { query: { clubId } })
    )
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await act.mutate(() => api.del(`/v1/staff/${deleting.id}`, { query: { clubId } }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <section className="detail-block">
      <SectionHead title="Сотрудники бекофиса" meta={`${rows.length} чел.`} />
      <div className="toolbar toolbar--compact">
        <div className="toolbar__left">
          <span className="muted">Доступ в бекофис этого клуба</span>
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
            {Icon.plus}
            Добавить сотрудника
          </button>
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Сотрудников ещё нет. Добавьте первого." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Создан</th>
                <th style={{ width: 260 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} style={s.disabledAt ? { opacity: 0.6 } : undefined}>
                  <td>{s.name}</td>
                  <td>{s.email}</td>
                  <td>{STAFF_ROLE_LABEL[s.role] ?? s.role}</td>
                  <td>
                    {s.disabledAt ? (
                      <span style={{ color: 'var(--amber)' }}>заблокирован</span>
                    ) : (
                      <span style={{ color: 'var(--green)' }}>активен</span>
                    )}
                  </td>
                  <td>{dateOnly(s.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(s)}
                        title="Имя, e-mail и роль сотрудника"
                      >
                        Изменить
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={act.saving}
                        onClick={() => void onToggleDisabled(s)}
                        title={
                          s.disabledAt
                            ? 'Вернуть доступ в бекофис'
                            : 'Запретить вход, сохранив аккаунт и историю'
                        }
                      >
                        {s.disabledAt ? 'Разблокировать' : 'Заблокировать'}
                      </button>
                      <IconButton
                        danger
                        title={`Удалить сотрудника «${s.name}»`}
                        aria-label="Удалить сотрудника"
                        onClick={() => setDeleting(s)}
                      >
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <StaffForm
          clubId={clubId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            list.reload()
          }}
        />
      )}

      {editing && (
        <StaffEditForm
          clubId={clubId}
          staff={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить сотрудника"
          message={`Удалить доступ «${deleting.name}» (${deleting.email})? Если нужно лишь закрыть вход, лучше заблокировать — тогда история действий останется связанной с аккаунтом.`}
          confirmLabel="Удалить"
          busy={act.saving}
          error={act.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}

      {act.error && !deleting && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {act.error}
        </div>
      )}
    </section>
  )
}

/**
 * Правка сотрудника super_admin'ом: имя, роль и сброс пароля. Пароль
 * необязателен — пустое поле означает «не менять».
 *
 * Роль «Владелец» здесь доступна (в отличие от самообслуживания владельца):
 * поддержке нужно уметь передать клуб другому человеку. Последнего активного
 * владельца сервер понизить/заблокировать не даст.
 */
function StaffEditForm({
  clubId,
  staff,
  onClose,
  onSaved
}: {
  clubId: string
  staff: Staff
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [name, setName] = useState(staff.name)
  const [role, setRole] = useState(staff.role)
  const [password, setPassword] = useState('')
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const body: Record<string, unknown> = {}
    if (name.trim() && name.trim() !== staff.name) body.name = name.trim()
    if (role !== staff.role) body.role = role
    if (password.trim()) body.password = password.trim()
    if (Object.keys(body).length === 0) {
      onClose()
      return
    }
    const ok = await save.mutate(() =>
      api.patch(`/v1/staff/${staff.id}`, body, { query: { clubId } })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={`Сотрудник: ${staff.email}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button">
            Отмена
          </button>
          <button
            className="btn btn--primary"
            form="staff-edit-form"
            type="submit"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      <form id="staff-edit-form" onSubmit={(e) => void onSubmit(e)}>
        <label className="field">
          <span className="field__label">Имя</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Роль</span>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as Staff['role'])}
          >
            <option value="owner">Владелец</option>
            <option value="admin">Администратор</option>
            <option value="accountant">Бухгалтер</option>
            <option value="viewer">Наблюдатель</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Новый пароль (пусто — не менять)</span>
          <input
            className="input"
            type="text"
            autoComplete="new-password"
            placeholder="минимум 6 символов"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {save.error && (
          <div className="form-error" style={{ marginTop: 12 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

function StaffForm({
  clubId,
  onClose,
  onSaved
}: {
  clubId: string
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<StaffRoleClub>('admin')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    if (!trimmedEmail) return setValidation('Укажите email.')
    if (password.length < 6) return setValidation('Пароль не короче 6 символов.')
    if (!trimmedName) return setValidation('Укажите имя сотрудника.')
    setValidation(null)

    const body = { email: trimmedEmail, password, name: trimmedName, role }
    const ok = await save.mutate(() => api.post(`/v1/clubs/${clubId}/staff`, body))
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Новый сотрудник"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            type="submit"
            form="staff-form"
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Создать
          </button>
        </>
      }
    >
      <form id="staff-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="s-email">
              Email
            </label>
            <input
              id="s-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@club.by"
              autoFocus
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="s-password">
              Пароль
            </label>
            <input
              id="s-password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="минимум 6 символов"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="s-role">
              Роль
            </label>
            <select
              id="s-role"
              className="input"
              value={role}
              onChange={(e) => setRole(e.target.value as StaffRoleClub)}
            >
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>
                  {STAFF_ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--full">
            <label className="field__label" htmlFor="s-name">
              Имя
            </label>
            <input
              id="s-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Иван Петров"
            />
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
